import React, { useState, useEffect } from 'react';
import {
  Modal,
  Form,
  Select,
  Input,
  Button,
  message,
  Space,
  Checkbox,
  Switch,
  Tag,
  Empty,
  Spin,
} from 'antd';
import {
  MailOutlined,
  WhatsAppOutlined,
  PhoneOutlined,
  PlusOutlined,
  ClockCircleOutlined,
  CheckCircleOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import axios from 'axios';

/**
 * Schedule Reminder Modal Component
 *
 * Props:
 * - visible: boolean - controls modal visibility
 * - onClose: function - callback when modal closes (cancel)
 * - onSave: function(reminders, contacts) - callback when saving reminders (for new invoices)
 * - paymentTermId: string/number - selected payment term ID
 * - invoiceDueDate: dayjs object - invoice due date for calculating reminder dates
 * - invoiceId: string - invoice UUID (null for new, UUID for existing)
 * - existingReminders: array - previously saved reminders (from parent state for new invoices)
 * - existingContacts: object - previously saved contact info (from parent state for new invoices)
 * - invoiceDisplay: string - display name for the invoice (e.g., "INV-2024-001")
 */

// Country codes for phone inputs
const COUNTRY_CODES = [
  { code: '+1', label: '+1 US/CA' },
  { code: '+44', label: '+44 UK' },
  { code: '+91', label: '+91 IN' },
  { code: '+61', label: '+61 AU' },
  { code: '+49', label: '+49 DE' },
  { code: '+33', label: '+33 FR' },
  { code: '+81', label: '+81 JP' },
  { code: '+86', label: '+86 CN' },
];

const ScheduleReminderModal = ({
  visible,
  onClose,
  onSave,
  paymentTermId,
  invoiceDueDate,
  invoiceId,
  existingReminders = [],
  existingContacts = {},
  invoiceDisplay = 'Invoice',
}) => {
  const [form] = Form.useForm();
  const [messageApi, contextHolder] = message.useMessage();

  // State
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [isEnabled, setIsEnabled] = useState(true);
  const [reminders, setReminders] = useState([]);
  const [reminderHistory, setReminderHistory] = useState([]);
  const [scheduleId, setScheduleId] = useState(null);
  const [, setPaymentTermName] = useState('');
  const [paymentTermDays, setPaymentTermDays] = useState(null);

  // Payment Term state
  const [paymentTerms, setPaymentTerms] = useState([]);
  const [selectedPaymentTermId, setSelectedPaymentTermId] = useState(paymentTermId);

  // Contact input states
  const [emailInput, setEmailInput] = useState('');
  const [emails, setEmails] = useState([]);
  const [ccInput, setCcInput] = useState('');
  const [ccEmails, setCcEmails] = useState([]);
  const [whatsappCountryCode, setWhatsappCountryCode] = useState('+1');
  const [whatsappInput, setWhatsappInput] = useState('');
  const [whatsappNumbers, setWhatsappNumbers] = useState([]);
  const [phoneCountryCode, setPhoneCountryCode] = useState('+1');
  const [phoneInput, setPhoneInput] = useState('');
  const [phoneNumbers, setPhoneNumbers] = useState([]);

  // Calculate reminder date based on due date and offset
  const calculateReminderDate = (dueDate, offset, triggerType) => {
    if (!dueDate) return null;
    const dueDateObj = dayjs.isDayjs(dueDate) ? dueDate : dayjs(dueDate);

    // Day 0 (offset = 0 with DUE trigger type) should show current date/time
    // This aligns with backend behavior where Day 0 reminders are sent immediately
    if (offset === 0 && triggerType === 'DUE') {
      return dayjs(); // Return current date/time
    }

    if (triggerType === 'DUE') {
      return dueDateObj;
    } else if (triggerType === 'OVERDUE') {
      return dueDateObj.add(offset, 'day');
    } else {
      // PRE_DUE or other types
      return dueDateObj.subtract(offset, 'day');
    }
  };

  // Get description for trigger type
  const getTriggerDescription = (triggerType, offset) => {
    switch (triggerType) {
      case 'PRE_DUE':
        return `${offset} days before due date`;
      case 'DUE':
        // Day 0 sends immediately
        return offset === 0 ? 'Immediately (Day 0)' : 'On due date';
      case 'OVERDUE':
        return `${offset} days after due date`;
      default:
        return `${offset} days`;
    }
  };

  // Fetch payment terms list
  const fetchPaymentTerms = async () => {
    try {
      const response = await axios.get('/masters/payment-terms/');
      if (response.data) {
        setPaymentTerms(Array.isArray(response.data) ? response.data : response.data.results || []);
      }
    } catch (error) {
      console.error('Error fetching payment terms:', error);
    }
  };

  // Fetch rules by payment term (for NEW invoices)
  const fetchRulesByPaymentTerm = async (termId) => {
    try {
      setLoading(true);
      const response = await axios.get('/reminders/payment-term-rules/by_payment_term/', {
        params: { payment_term_id: termId, entity_type: 'INVOICE' }
      });

      if (response.data) {
        setPaymentTermName(response.data.payment_term_name || '');
        setPaymentTermDays(response.data.payment_term_days);

        if (response.data.rules_detail && response.data.rules_detail.length > 0) {
          const calculatedReminders = response.data.rules_detail.map((rule, index) => ({
            id: `rule-${rule.id || index}`,
            name: rule.name,
            triggerType: rule.trigger_type,
            triggerTypeDisplay: rule.trigger_type_display,
            offset: rule.offset_amount,
            date: calculateReminderDate(invoiceDueDate, rule.offset_amount, rule.trigger_type),
            enabled: true,
            ruleId: rule.id,
          }));
          setReminders(calculatedReminders);
        }
      }
    } catch (error) {
      console.error('Error fetching payment term rules:', error);
    } finally {
      setLoading(false);
    }
  };

  // Fetch existing schedule (for EXISTING invoices)
  const fetchExistingSchedule = async (invId) => {
    try {
      setLoading(true);
      const response = await axios.get('/reminders/schedules/by_entity/', {
        params: { entity_type: 'invoice', entity_id: invId }
      });

      if (response.data) {
        setScheduleId(response.data.id);
        setIsEnabled(response.data.is_enabled !== false);

        // Set contact info
        if (response.data.emails) setEmails(response.data.emails);
        if (response.data.emails_cc) setCcEmails(response.data.emails_cc);
        if (response.data.whatsapp_numbers) setWhatsappNumbers(response.data.whatsapp_numbers);
        if (response.data.phone_numbers) setPhoneNumbers(response.data.phone_numbers);

        // Fetch rules for this schedule if available
        if (paymentTermId) {
          await fetchRulesByPaymentTerm(paymentTermId);
        }
      }
    } catch (error) {
      console.error('Error fetching existing schedule:', error);
    } finally {
      setLoading(false);
    }
  };

  // Fetch reminder history
  const fetchReminderHistory = async (invId) => {
    try {
      const response = await axios.get('/reminders/logs/by_entity/', {
        params: { entity_type: 'invoice', entity_id: invId }
      });

      if (response.data) {
        setReminderHistory(Array.isArray(response.data) ? response.data : []);
      }
    } catch (error) {
      console.error('Error fetching reminder history:', error);
    }
  };

  // Initialize when modal opens
  useEffect(() => {
    if (visible) {
      // Reset state
      setReminders([]);
      setReminderHistory([]);
      setScheduleId(null);
      setPaymentTermName('');
      setPaymentTermDays(null);
      setSelectedPaymentTermId(paymentTermId);

      // Always fetch payment terms for dropdown
      fetchPaymentTerms();

      if (invoiceId) {
        // Existing invoice - fetch schedule and history
        fetchExistingSchedule(invoiceId);
        fetchReminderHistory(invoiceId);
        // Also fetch rules if payment term is set
        if (paymentTermId) {
          fetchRulesByPaymentTerm(paymentTermId);
        }
      } else if (paymentTermId) {
        // New invoice - fetch rules by payment term
        fetchRulesByPaymentTerm(paymentTermId);

        // Use existing data from parent if available
        if (existingReminders?.length > 0) {
          setReminders(existingReminders);
        }
        if (existingContacts) {
          setEmails(existingContacts.emails || []);
          setCcEmails(existingContacts.emailCC || []);
          setWhatsappNumbers(existingContacts.whatsappNumbers || []);
          setPhoneNumbers(existingContacts.phoneNumbers || []);
        }
      }
    }
  }, [visible, invoiceId, paymentTermId]);

  // Toggle reminder enabled state
  const handleToggleReminder = (reminderId) => {
    setReminders((prev) =>
      prev.map((r) => (r.id === reminderId ? { ...r, enabled: !r.enabled } : r))
    );
  };

  // Email validation
  const emailValidation = (email) => {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return re.test(email);
  };

  // Add email
  const handleAddEmail = () => {
    if (emailInput && emailValidation(emailInput)) {
      if (!emails.includes(emailInput)) {
        setEmails([...emails, emailInput]);
      }
      setEmailInput('');
    } else if (emailInput) {
      messageApi.warning('Please enter a valid email address');
    }
  };

  // Remove email
  const handleRemoveEmail = (email) => {
    setEmails(emails.filter((e) => e !== email));
  };

  // Add CC email
  const handleAddCcEmail = () => {
    if (ccInput && emailValidation(ccInput)) {
      if (!ccEmails.includes(ccInput)) {
        setCcEmails([...ccEmails, ccInput]);
      }
      setCcInput('');
    } else if (ccInput) {
      messageApi.warning('Please enter a valid email address');
    }
  };

  // Remove CC email
  const handleRemoveCcEmail = (email) => {
    setCcEmails(ccEmails.filter((e) => e !== email));
  };

  // Add WhatsApp number
  const handleAddWhatsapp = () => {
    if (whatsappInput) {
      const fullNumber = `${whatsappCountryCode} ${whatsappInput}`;
      if (!whatsappNumbers.includes(fullNumber)) {
        setWhatsappNumbers([...whatsappNumbers, fullNumber]);
      }
      setWhatsappInput('');
    }
  };

  // Remove WhatsApp number
  const handleRemoveWhatsapp = (number) => {
    setWhatsappNumbers(whatsappNumbers.filter((n) => n !== number));
  };

  // Add phone number
  const handleAddPhone = () => {
    if (phoneInput) {
      const fullNumber = `${phoneCountryCode} ${phoneInput}`;
      if (!phoneNumbers.includes(fullNumber)) {
        setPhoneNumbers([...phoneNumbers, fullNumber]);
      }
      setPhoneInput('');
    }
  };

  // Remove phone number
  const handleRemovePhone = (number) => {
    setPhoneNumbers(phoneNumbers.filter((n) => n !== number));
  };

  // Handle save
  const handleSave = async () => {
    const activeReminders = reminders.filter((r) => r.enabled);

    if (activeReminders.length === 0) {
      messageApi.warning('Please enable at least one reminder');
      return;
    }

    setSaving(true);
    try {
      const contactData = {
        emails,
        emailCC: ccEmails,
        whatsappNumbers,
        phoneNumbers,
      };

      if (invoiceId) {
        // Existing invoice - create or update schedule
        const payload = {
          entity_type: 'INVOICE',
          invoices: invoiceId,
          is_enabled: isEnabled,
          emails,
          emails_cc: ccEmails,
          whatsapp_numbers: whatsappNumbers,
          phone_numbers: phoneNumbers,
          rules: activeReminders.map((r) => r.ruleId).filter(Boolean),
        };

        if (scheduleId) {
          // Update existing schedule
          await axios.patch('/reminders/schedules/invoices/', payload);
          messageApi.success('Reminders updated successfully');
        } else {
          // Create new schedule for existing invoice
          await axios.post('/reminders/schedules/invoices/', payload);
          messageApi.success('Reminders created successfully');
        }
      } else {
        // For new invoices, pass data back to parent
        onSave(activeReminders, contactData);
        messageApi.success('Reminders configured successfully');
      }

      onClose();
    } catch (error) {
      messageApi.error('Failed to save reminders');
      console.error('Error saving reminders:', error);
    } finally {
      setSaving(false);
    }
  };

  // Handle cancel
  const handleCancel = () => {
    form.resetFields();
    onClose();
  };

  // Get channel display info
  const getChannelInfo = (channel) => {
    switch (channel?.toUpperCase()) {
      case 'EMAIL':
        return { icon: <MailOutlined />, color: '#1890ff', label: 'Email Reminder' };
      case 'WHATSAPP':
        return { icon: <WhatsAppOutlined />, color: '#25D366', label: 'WhatsApp Reminder' };
      case 'SMS':
        return { icon: <PhoneOutlined />, color: '#722ed1', label: 'SMS Reminder' };
      default:
        return { icon: <MailOutlined />, color: '#1890ff', label: 'Reminder' };
    }
  };

  const dueDateDisplay = invoiceDueDate
    ? (dayjs.isDayjs(invoiceDueDate) ? invoiceDueDate.format('YYYY-MM-DD') : dayjs(invoiceDueDate).format('YYYY-MM-DD'))
    : '';

  return (
    <>
      {contextHolder}
      <Modal
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
            <span>Schedule Payment Reminders</span>
            <Switch
              checked={isEnabled}
              onChange={setIsEnabled}
              checkedChildren="Enabled"
              unCheckedChildren="Disabled"
            />
          </div>
        }
        open={visible}
        onCancel={handleCancel}
        width={700}
        footer={null}
      >
        <Spin spinning={loading}>
          <Form form={form} layout="vertical" style={{ marginTop: 16 }}>
            {/* Payment Term Selection */}
            <div style={{ marginBottom: 24 }}>
              <label style={{ display: 'block', marginBottom: 8, fontWeight: 500 }}>
                Payment Term
              </label>
              <Select
                value={selectedPaymentTermId}
                onChange={(value) => {
                  setSelectedPaymentTermId(value);
                  setReminders([]);
                  fetchRulesByPaymentTerm(value);
                }}
                style={{ width: '100%' }}
                placeholder="Select payment term to load reminder rules"
              >
                {paymentTerms.map((term) => (
                  <Select.Option key={term.id} value={term.id}>
                    {term.name} ({term.duration_in_days} days)
                  </Select.Option>
                ))}
              </Select>
            </div>

            {/* Contact Information Section */}
            <div style={{ marginBottom: 24 }}>
              <h4 style={{ marginBottom: 16, fontWeight: 600 }}>Contact Information</h4>

              {/* Email To */}
              <div style={{ marginBottom: 16 }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                  <MailOutlined style={{ color: '#1890ff' }} />
                  Email To
                </label>
                <div style={{ display: 'flex', gap: 8 }}>
                  <Input
                    placeholder="client@example.com"
                    value={emailInput}
                    onChange={(e) => setEmailInput(e.target.value)}
                    onPressEnter={handleAddEmail}
                    style={{ flex: 1 }}
                  />
                  <Button type="primary" icon={<PlusOutlined />} onClick={handleAddEmail} />
                </div>
                <div style={{ marginTop: 8, display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                  {emails.map((email) => (
                    <Tag
                      key={email}
                      closable
                      onClose={() => handleRemoveEmail(email)}
                      color="blue"
                    >
                      {email}
                    </Tag>
                  ))}
                </div>
              </div>

              {/* CC */}
              <div style={{ marginBottom: 16 }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                  <MailOutlined style={{ color: '#1890ff' }} />
                  CC
                </label>
                <div style={{ display: 'flex', gap: 8 }}>
                  <Input
                    placeholder="cc@example.com (optional)"
                    value={ccInput}
                    onChange={(e) => setCcInput(e.target.value)}
                    onPressEnter={handleAddCcEmail}
                    style={{ flex: 1 }}
                  />
                  <Button type="primary" icon={<PlusOutlined />} onClick={handleAddCcEmail} />
                </div>
                <div style={{ marginTop: 8, display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                  {ccEmails.map((email) => (
                    <Tag
                      key={email}
                      closable
                      onClose={() => handleRemoveCcEmail(email)}
                      color="blue"
                    >
                      {email}
                    </Tag>
                  ))}
                </div>
              </div>

              {/* WhatsApp */}
              <div style={{ marginBottom: 16 }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                  <WhatsAppOutlined style={{ color: '#25D366' }} />
                  WhatsApp
                </label>
                <div style={{ display: 'flex', gap: 8 }}>
                  <Select
                    value={whatsappCountryCode}
                    onChange={setWhatsappCountryCode}
                    style={{ width: 120 }}
                  >
                    {COUNTRY_CODES.map((c) => (
                      <Select.Option key={c.code} value={c.code}>{c.label}</Select.Option>
                    ))}
                  </Select>
                  <Input
                    placeholder="234 567 8900"
                    value={whatsappInput}
                    onChange={(e) => setWhatsappInput(e.target.value)}
                    onPressEnter={handleAddWhatsapp}
                    style={{ flex: 1 }}
                  />
                  <Button type="primary" icon={<PlusOutlined />} onClick={handleAddWhatsapp} />
                </div>
                <div style={{ marginTop: 8, display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                  {whatsappNumbers.map((number) => (
                    <Tag
                      key={number}
                      closable
                      onClose={() => handleRemoveWhatsapp(number)}
                      color="green"
                    >
                      {number}
                    </Tag>
                  ))}
                </div>
              </div>

              {/* Phone Number */}
              <div style={{ marginBottom: 16 }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                  <PhoneOutlined style={{ color: '#722ed1' }} />
                  Phone Number
                </label>
                <div style={{ display: 'flex', gap: 8 }}>
                  <Select
                    value={phoneCountryCode}
                    onChange={setPhoneCountryCode}
                    style={{ width: 120 }}
                  >
                    {COUNTRY_CODES.map((c) => (
                      <Select.Option key={c.code} value={c.code}>{c.label}</Select.Option>
                    ))}
                  </Select>
                  <Input
                    placeholder="234 567 8900"
                    value={phoneInput}
                    onChange={(e) => setPhoneInput(e.target.value)}
                    onPressEnter={handleAddPhone}
                    style={{ flex: 1 }}
                  />
                  <Button type="primary" icon={<PlusOutlined />} onClick={handleAddPhone} />
                </div>
                <div style={{ marginTop: 8, display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                  {phoneNumbers.map((number) => (
                    <Tag
                      key={number}
                      closable
                      onClose={() => handleRemovePhone(number)}
                      color="purple"
                    >
                      {number}
                    </Tag>
                  ))}
                </div>
              </div>
            </div>

            {/* Scheduled Reminders Section */}
            <div style={{ marginBottom: 24 }}>
              <h4 style={{ marginBottom: 8, fontWeight: 600 }}>Scheduled Reminders</h4>
              <p style={{ color: '#666', marginBottom: 16 }}>
                Based on payment term: {paymentTermDays || '—'} days (Due: {dueDateDisplay || '—'})
              </p>

              {!invoiceDueDate && (
                <div
                  style={{
                    background: '#fff7e6',
                    padding: '12px',
                    borderRadius: '8px',
                    marginBottom: '16px',
                  }}
                >
                  <p style={{ margin: 0, fontSize: '13px', color: '#d46b08' }}>
                    <strong>Note:</strong> Please set a due date on the invoice to enable reminder scheduling.
                  </p>
                </div>
              )}

              {reminders.length === 0 && invoiceDueDate && !loading && (
                <Empty
                  description="No reminders configured for this payment term."
                  style={{ marginBottom: 16 }}
                />
              )}

              {reminders.map((reminder) => (
                <div
                  key={reminder.id}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '12px 16px',
                    marginBottom: '8px',
                    background: reminder.enabled ? '#f0f5ff' : '#fafafa',
                    borderRadius: '8px',
                    border: `1px solid ${reminder.enabled ? '#adc6ff' : '#d9d9d9'}`,
                  }}
                >
                  <Space align="center">
                    <Checkbox
                      checked={reminder.enabled}
                      onChange={() => handleToggleReminder(reminder.id)}
                    />
                    <div>
                      <div style={{ fontWeight: 500, color: '#262626' }}>
                        {reminder.name || `Reminder ${reminder.offset} days`}
                      </div>
                      <div style={{ fontSize: '13px', color: '#8c8c8c' }}>
                        {getTriggerDescription(reminder.triggerType, reminder.offset)}
                      </div>
                    </div>
                  </Space>
                  <Space align="center" style={{ color: '#8c8c8c' }}>
                    <ClockCircleOutlined />
                    <span>
                      {reminder.date
                        ? (reminder.offset === 0 && reminder.triggerType === 'DUE')
                          ? `${reminder.date.format('YYYY-MM-DD HH:mm')} (Now)`
                          : reminder.date.format('YYYY-MM-DD')
                        : '—'}
                    </span>
                  </Space>
                </div>
              ))}
            </div>

            {/* Reminder History Section */}
            {invoiceId && (
              <div style={{ marginBottom: 24 }}>
                <h4 style={{ marginBottom: 16, fontWeight: 600 }}>Reminder History</h4>

                {reminderHistory.length === 0 ? (
                  <Empty description="No reminder history yet" style={{ marginBottom: 16 }} />
                ) : (
                  <div>
                    {reminderHistory.map((item) => {
                      const channelInfo = getChannelInfo(item.channel);
                      return (
                        <div
                          key={item.id}
                          style={{
                            display: 'flex',
                            alignItems: 'flex-start',
                            padding: '12px 16px',
                            marginBottom: '8px',
                            background: '#fff',
                            borderRadius: '8px',
                            border: '1px solid #e8e8e8',
                          }}
                        >
                          <div
                            style={{
                              width: 32,
                              height: 32,
                              borderRadius: '50%',
                              background: '#e6f7e6',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              marginRight: 12,
                              color: '#52c41a',
                            }}
                          >
                            <CheckCircleOutlined />
                          </div>
                          <div style={{ flex: 1 }}>
                            <div style={{ fontWeight: 500, color: channelInfo.color }}>
                              {channelInfo.label}
                              {item.reminder_rule_name && (
                                <span style={{ fontWeight: 400, color: '#666', marginLeft: 8 }}>
                                  ({item.reminder_rule_name})
                                </span>
                              )}
                            </div>
                            <div style={{ fontSize: '13px', color: '#666' }}>
                              To: {item.recipient_email || item.recipient_phone || item.recipient_whatsapp || '—'}
                            </div>
                            <div style={{ fontSize: '12px', color: '#8c8c8c' }}>
                              <ClockCircleOutlined style={{ marginRight: 4 }} />
                              Scheduled: {item.scheduled_at ? dayjs(item.scheduled_at).format('YYYY-MM-DD hh:mm A') : '—'}
                            </div>
                            {item.sent_at && (
                              <div style={{ fontSize: '12px', color: '#52c41a' }}>
                                <CheckCircleOutlined style={{ marginRight: 4 }} />
                                Sent: {dayjs(item.sent_at).format('YYYY-MM-DD hh:mm A')}
                              </div>
                            )}
                          </div>
                          <Tag color={item.status === 'SENT' || item.status === 'DELIVERED' ? 'green' : 'orange'}>
                            {item.status_display || item.status}
                          </Tag>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            {/* Footer Buttons */}
            <div
              style={{
                display: 'flex',
                justifyContent: 'flex-end',
                gap: '8px',
                marginTop: '24px',
                paddingTop: '16px',
                borderTop: '1px solid #f0f0f0',
              }}
            >
              <Button onClick={handleCancel}>Cancel</Button>
              <Button
                type="primary"
                onClick={handleSave}
                loading={saving}
                disabled={!invoiceDueDate || reminders.filter((r) => r.enabled).length === 0}
              >
                Save Reminders
              </Button>
            </div>
          </Form>
        </Spin>
      </Modal>
    </>
  );
};

export default ScheduleReminderModal;
